import React, {
    useState,
    useEffect,
    useContext,
    Dispatch,
    SetStateAction,
} from 'react';
import {
    ShimmeredDetailsList,
    SelectionMode,
    DetailsListLayoutMode,
    IColumn,
    Stack,
    IButtonStyles,
    DEFAULT_CELL_STYLE_PROPS,
} from '@fluentui/react';
import { MultiStageReleaseSearch } from '../MultiStageReleaseSearch';
import { ServiceNameDropDown } from '../../../../Common/components/ServiceNameDropDown';
import { CloudNameDropDown } from '../../../../Common/components/CloudNameDropDown';
import { DeploymentTypeDropDown } from '../../../../Common/components/DeploymentTypeDropDown';
import { commonGridStyle } from '../../../../Common/util/tableUtils';
import { CapabilityContext } from '../../../../Common/components/Capabilities/CapabilityContext';
import { Capability } from '../../../../Common/components/Capabilities/capability';

import { dateLocaleStringWithTimeZone } from '../../../../Common/util/DateUtils';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import {
    clearAllSelectedFields,
    getThemeFromString,
    setMultiStageReleaseSearchValueInLocalStorage,
} from '../../../../Common/util/localStorageUtils';
import {
    ExternalLink,
    InternalLink,
} from '../../../../Common/components/Links';
import { getReleaseDetailsPath } from '../../../util/ReleaseUtils';
import { ReleaseSearchResult } from '../../../models/Release';
import {
    getAdoReleaseUrl,
    getNewYamlReleaseUrl,
    getServiceTreeUrl,
} from '../../../../Common/util/RouteUtils';
import { CloudStatusDisplay } from './Status/CloudStatusDisplay';
import { PageContent } from '../../../../HubLayout/components/PageLayout/PageContent';
import { PageTitle } from '../../../../HubLayout/components/PageLayout/PageTitle';
import { DefaultButton } from 'office-ui-fabric-react';
import { ReleaseExporter } from '../../../../Administration/components/Importer/ReleaseExporter';
import { useIsMobile } from '../../../../Common/hooks/useIsMobile';
import { MobileReleaseList } from './Mobile/MobileReleaseList';
import { ReleasePipelineSource } from '../../../models/ReleasePipelineSource';
import { ReleaseColumns } from '../../../../HubLayout/components/ReleaseColumns';
import { getReleasePipelineSourceTag } from './Status/ReleasePipelineSourceTag';
import { DateRangeDropDown } from '../../../../Common/components/DateRangeDropDown';
import { ClearFieldsButton } from '../../../../Common/components/ClearFieldsButton';
import { AutoRefresh } from '../AutoRefresh';

interface IReleaseListProps {
    list: ReleaseSearchResult[];
    isLoaded: boolean;
    hideButton?: boolean;
    pageNumber?: number;
    value?: string;
    searchQuery?: string;
    setSearchQuery?: Dispatch<SetStateAction<string | undefined>>;
    searchValue?: string;
    setSearchValue?: Dispatch<SetStateAction<string | undefined>>;
    selectedServices?: string[];
    setSelectedServices?: Dispatch<SetStateAction<string[]>>;
    selectedEnvironments?: string[];
    setSelectedEnvironments?: Dispatch<SetStateAction<string[]>>;
    endDate?: string;
    setEndDate?: Dispatch<SetStateAction<string | undefined>>;
    startDate?: string;
    setStartDate?: Dispatch<SetStateAction<string | undefined>>;
    selectedDeploymentTypes?: string[];
    setSelectedDeploymentTypes?: Dispatch<SetStateAction<string[]>>;
    setSelectedProjects?: Dispatch<SetStateAction<string[]>>;
    setPageNumber?: Dispatch<SetStateAction<number>>;
    // When provided, the auto-refresh control is shown above the list and this
    // callback is invoked each time the selected interval elapses. The interval
    // is shared with the Release Details page via localStorage.
    onAutoRefresh?: () => void;
}

export const ReleaseList: React.FC<IReleaseListProps> = (
    props: IReleaseListProps
) => {
    const [columnsList, setColumnsList] = useState<IColumn[]>([]);
    const [resizedColumnWidths, setResizedColumnWidths] = useState<
        Record<string, number>
    >({});
    const [items, setItems] = useState<ReleaseSearchResult[]>([]); // current version of the list - filtered or sorted
    const isMobile = useIsMobile();

    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const loadMoreButtonStyles: IButtonStyles = {
        root: {
            display: props.hideButton ? 'none' : 'block',
            width: '100%',
            marginTop: '10px',
            background: theme.palette.neutralLighter,
            color: theme.palette.neutralPrimary,
        },
        rootHovered: {
            color: theme.palette.neutralPrimaryAlt,
            backgroundColor: theme.palette.neutralLight,
        },
    };

    // Use capabilities context
    const capabilities = useContext(CapabilityContext);

    // Only show ADO links in public cloud
    const includeExternalLink = capabilities.check(Capability.public);

    const columns: IColumn[] = ReleaseColumns;
    const clearFiltersTrackWidth = 44;
    const releaseColumnWidths = columns.map(
        (column) =>
            (resizedColumnWidths[column.key] ??
                (column.maxWidth || column.minWidth)) +
            DEFAULT_CELL_STYLE_PROPS.cellLeftPadding +
            DEFAULT_CELL_STYLE_PROPS.cellRightPadding
    );

    // DetailsList owns the width once a divider is dragged, so mirror it here.
    const onColumnResize = (column?: IColumn, newWidth?: number) => {
        if (!column || !newWidth) {
            return;
        }

        setResizedColumnWidths((current) => ({
            ...current,
            [column.key]: newWidth,
        }));
    };
    const releaseColumnTracks = releaseColumnWidths
        .map((width) => `${width}px`)
        .join(' ');
    // Filter row and table must resolve to the same width so they stay aligned
    // while the page scrolls horizontally.
    const releaseContentWidth =
        releaseColumnWidths.reduce((total, width) => total + width, 0) +
        clearFiltersTrackWidth;
    // PageContent's horizontal padding (5) and inner margin (20) on each side.
    const pageContentGutter = 50;

    const releaseFilterGridStyles: React.CSSProperties = {
        display: 'grid',
        // Trailing track holds the clear button, which has no matching column.
        gridTemplateColumns: `${releaseColumnTracks} ${clearFiltersTrackWidth}px`,
        alignItems: 'start',
        width: '100%',
        paddingTop: 5,
        backgroundColor: theme.palette.neutralLighter,
        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
    };

    const renderReleaseColumn = (
        item: ReleaseSearchResult,
        index?: number,
        column?: IColumn
    ) => {
        const fieldContent = item[
            column?.fieldName as keyof ReleaseSearchResult
        ] as string;

        const getAdoReleaseItem = (): string => {
            return getAdoReleaseUrl(
                item.accountName || item.adoInstance || '',
                item.projectName || '',
                '',
                item.releaseId
            );
        };

        const getNewYAMLLink = (): string => {
            return getNewYamlReleaseUrl(
                item?.accountName || item?.adoInstance || '',
                item?.projectName || '',
                '',
                item?.releaseId || 0
            );
        };

        switch (column?.key) {
            case 'service':
                return (
                    <div>
                        {item.serviceTreeName || item.serviceTreeId}
                    </div>
                );

            case 'releaseId':
                return (
                    <div>
                        <InternalLink
                            value={
                                item.releaseName
                                    ? item.releaseName
                                    : 'Release-' + item.releaseId
                            }
                            url={
                                '#' +
                                getReleaseDetailsPath(
                                    item.releaseCorrelationId || ''
                                )
                            }
                            title="View release details"
                        />
                        <div
                            style={{
                                color: theme.palette.neutralSecondary,
                                fontSize: 11, //FontSizes.size12 looks too big
                            }}
                        >
                            <div>{'Id: ' + item.releaseId}</div>
                            <div>
                                {getReleasePipelineSourceTag(
                                    item.releasePipelineSource
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 'environments':
                return (
                    <CloudStatusDisplay
                        cloudStatuses={item.cloudStatuses}
                        environments={item.environments}
                    />
                );

            case 'createdOn':
                return dateLocaleStringWithTimeZone(item.createdOn);

            case 'updatedOn':
                return dateLocaleStringWithTimeZone(item.updatedOn);

            default:
                return <span>{fieldContent}</span>;
        }
    };

    // When parent component sends new data, refresh my list accordingly
    useEffect(() => {
        // Default sort by latest timestamp
        //let releases = props.list;
        //releases.sort((s1, s2) => {
        //    if (s1.updatedOn && s2.updatedOn) {
        //        return new Date(dateLocaleStringWithTimeZone(s1.updatedOn)) >
        //            new Date(dateLocaleStringWithTimeZone(s2.updatedOn))
        //            ? -1
        //            : 1;
        //    }
        //    return 0;
        //});

        setColumnsList(columns);
        setItems(props.list);
        // needs columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.list, props.isLoaded]);

    const loadMore = () => {
        if (props.setPageNumber && props.pageNumber) {
            props.setPageNumber(props.pageNumber + 1);
        }
    };

    const clearAllFields = () => {
        props.setSearchValue?.('');
        props.setSelectedEnvironments?.([]);
        props.setSelectedServices?.([]);
        props.setSelectedDeploymentTypes?.([]);
        props.setStartDate?.(undefined);
        props.setEndDate?.(undefined);
        props.setPageNumber?.(1);
        clearAllSelectedFields(); //clears the cached fields
        props.setSearchQuery?.('');
    };

    return (
        <div
            className="ReleaseList-root"
            style={
                isMobile
                    ? undefined
                    : {
                          minWidth: releaseContentWidth + pageContentGutter,
                          backgroundColor: theme.surfaces.appBackground,
                      }
            }
        >
            <Stack
                horizontal
                verticalAlign="start"
                style={{ justifyContent: 'space-between' }}
            >
                <Stack>
                    <PageTitle
                        title="Latest Release Statuses"
                        subTitle={
                            props.isLoaded
                                ? items.length + ' latest releases'
                                : 'Loading...'
                        }
                    />

                    {capabilities.check(Capability.srmAdminRole) && (
                        <ReleaseExporter
                            releaseCorrelationIds={items.map(
                                (item) => item.releaseCorrelationId
                            )}
                        />
                    )}
                </Stack>
                {!isMobile && props.onAutoRefresh && (
                    <AutoRefresh onRefresh={props.onAutoRefresh} />
                )}
            </Stack>
            {isMobile ? (
                <MobileReleaseList
                    list={items}
                    isLoaded={props.isLoaded}
                    pageNumber={props.pageNumber}
                    hideButton={props.hideButton}
                    searchQuery={props.searchQuery}
                    setSearchQuery={props.setSearchQuery}
                    searchValue={props.searchValue}
                    setSearchValue={props.setSearchValue}
                    selectedServices={props.selectedServices}
                    setSelectedServices={props.setSelectedServices}
                    selectedEnvironments={props.selectedEnvironments}
                    setSelectedEnvironments={props.setSelectedEnvironments}
                    selectedDeploymentTypes={props.selectedDeploymentTypes}
                    setSelectedDeploymentTypes={
                        props.setSelectedDeploymentTypes
                    }
                    startDate={props.startDate}
                    setStartDate={props.setStartDate}
                    endDate={props.endDate}
                    setEndDate={props.setEndDate}
                    setSelectedProjects={props.setSelectedProjects}
                    setPageNumber={props.setPageNumber}
                    clearAllFields={clearAllFields}
                />
            ) : (
                <div>
                    <PageContent>
                        <div style={releaseFilterGridStyles}>
                            <MultiStageReleaseSearch
                                searchValue={props.searchValue ?? ''}
                                setSearchValue={props.setSearchValue}
                                setPageNumber={props.setPageNumber}
                                isLoaded={props.isLoaded}
                                setSearchQuery={props.setSearchQuery}
                                placeholder="Release / Project / Component Name"
                                alignToGrid
                                gridColumn="span 3"
                            />
                            {/* Wrapped so the grid cell survives an empty lookup. */}
                            <div>
                                <ServiceNameDropDown
                                    selectedServices={
                                        props.selectedServices ?? []
                                    }
                                    setSelectedServices={
                                        props.setSelectedServices
                                    }
                                    setPageNumber={props.setPageNumber}
                                    isLoaded={props.isLoaded}
                                    placeholder="Service"
                                />
                            </div>
                            <div>
                                <CloudNameDropDown
                                    selectedEnvironments={
                                        props.selectedEnvironments ?? []
                                    }
                                    setSelectedEnvironments={
                                        props.setSelectedEnvironments
                                    }
                                    setPageNumber={props.setPageNumber}
                                    isLoaded={props.isLoaded}
                                    placeholder="Environment(s)"
                                />
                            </div>
                            <div>
                                <DeploymentTypeDropDown
                                    selectedDeploymentTypes={
                                        props.selectedDeploymentTypes ?? []
                                    }
                                    setSelectedDeploymentTypes={
                                        props.setSelectedDeploymentTypes
                                    }
                                    setPageNumber={props.setPageNumber}
                                    isLoaded={props.isLoaded}
                                    placeholder="Deployment Type"
                                />
                            </div>
                            <DateRangeDropDown
                                isStartDate={true}
                                startDate={props.startDate ?? undefined}
                                endDate={props.endDate ?? undefined}
                                setStartDate={props.setStartDate}
                                placeholder="Created On Start"
                                alignToGrid
                            />
                            <DateRangeDropDown
                                isStartDate={false}
                                startDate={props.startDate ?? undefined}
                                endDate={props.endDate ?? undefined}
                                setEndDate={props.setEndDate}
                                placeholder="Created On End"
                                alignToGrid
                            />
                            <ClearFieldsButton
                                isLoaded={props.isLoaded}
                                clearAllFields={clearAllFields}
                            />
                        </div>

                        <div
                            style={{
                                width: '100%',
                            }}
                        >
                            <ShimmeredDetailsList
                                className={commonGridStyle(theme)}
                                items={items}
                                columns={columnsList}
                                onColumnResize={onColumnResize}
                                onRenderItemColumn={renderReleaseColumn}
                                selectionMode={SelectionMode.none}
                                layoutMode={DetailsListLayoutMode.fixedColumns}
                                enableShimmer={!props.isLoaded}
                                shimmerLines={10}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Releases"
                            />
                        </div>
                    </PageContent>

                    <DefaultButton
                        styles={loadMoreButtonStyles}
                        text={'Load More'}
                        onClick={loadMore}
                    />

                    {props.isLoaded &&
                        (props.list.length < 1 || items.length < 1) && (
                            <p>No data found.</p>
                        )}
                </div>
            )}
        </div>
    );
};
